/**
 * `resoudreDessin(dessin) → DessinResolu` — le registre des dessins de fiche,
 * RÉSOLU en liste d'affichage (MV-SAV-02).
 *
 * ═══ Pourquoi ici, dans `@silex/shared` ═══
 *
 * Pour la raison qui y a mis `resoudreFigure` et `disposerMath` : deux rendus
 * consomment la même liste — le SVG natif de l'application et le SVG web de
 * la console (MV-SAV-04). Un escalier dessiné deux fois finirait par ne plus
 * avoir les mêmes marches. Ici, un dessin de fiche devient une suite de
 * TRACES élémentaires (rectangle, polygone, cercle, chemin, texte, formule) ;
 * un rendu n'a plus qu'à les peindre, chacune avec le jeton de son rôle.
 *
 * Fonction PURE, sans dépendance hors du moteur de formules. Aucune couleur :
 * chaque trace porte un RÔLE (`main`, `teinte`, `efface`…), que le rendu
 * résout par les jetons — c'est ce qui donne le mode sombre et les quatre
 * accents sans une ligne de plus.
 *
 * ═══ Les unités ═══
 *
 * Tout est en unités de TOILE (720 de large par défaut). Le rendu pose un
 * `viewBox` et met à l'échelle de la colonne. Les constantes de mise en page
 * ci-dessous sont celles du kit de la maquette (`kit/dessin.tsx` et les sept
 * fiches) : c'est leur main qu'on reprend, pas une nouvelle.
 */
import { analyserTexteLatex } from '../math/latex.js';
import { disposerMath } from '../math/disposer.js';
import { LARGEUR_TOILE, } from '../types/fiche-cours.js';
/* ───────────────────────────────────────────────────────────── la plume */
const n1 = (v) => (Math.round(v * 10) / 10).toString();
const pt = (x, y) => `${n1(x)} ${n1(y)}`;
/** Une accolade horizontale, de `x1` à `x2`, la pointe du côté de `sens`. */
export function cheminAccolade(x1, x2, y, sens) {
    const s = sens === 'bas' ? 1 : -1;
    const m = (x1 + x2) / 2;
    const h = 6 * s;
    const r = Math.min(8, (x2 - x1) / 4);
    return [
        `M${pt(x1, y)}`,
        `Q${pt(x1, y + h)} ${pt(x1 + r, y + h)}`,
        `L${pt(m - r, y + h)}`,
        `Q${pt(m, y + h)} ${pt(m, y + 2 * h)}`,
        `Q${pt(m, y + h)} ${pt(m + r, y + h)}`,
        `L${pt(x2 - r, y + h)}`,
        `Q${pt(x2, y + h)} ${pt(x2, y)}`,
    ].join(' ');
}
/** La pointe d'une flèche qui arrive en `a` en venant de `depuis`. */
export function cheminPointe(a, depuis, longueur = 8, ouverture = 0.48) {
    const angle = Math.atan2(a[1] - depuis[1], a[0] - depuis[0]);
    const p1 = [
        a[0] + longueur * Math.cos(angle + Math.PI - ouverture),
        a[1] + longueur * Math.sin(angle + Math.PI - ouverture),
    ];
    const p2 = [
        a[0] + longueur * Math.cos(angle + Math.PI + ouverture),
        a[1] + longueur * Math.sin(angle + Math.PI + ouverture),
    ];
    return `M${pt(p1[0], p1[1])} L${pt(a[0], a[1])} L${pt(p2[0], p2[1])}`;
}
function fleche(de, par, a, trait) {
    return [
        {
            type: 'chemin',
            d: `M${pt(de[0], de[1])} Q${pt(par[0], par[1])} ${pt(a[0], a[1])}`,
            trait,
        },
        { type: 'chemin', d: cheminPointe(a, par), trait },
    ];
}
const segment = (x1, y1, x2, y2, trait) => ({
    type: 'chemin',
    d: `M${pt(x1, y1)} L${pt(x2, y2)}`,
    trait,
});
/** Interligne de l'écriture du prof, en proportion de son corps. */
const INTERLIGNE_MAIN = 24 / 21;
function main(x, y, texte, taille, ancre) {
    return texte.split('\n').map((ligne, i) => ({
        type: 'texte',
        x,
        y: y + i * Math.round(taille * INTERLIGNE_MAIN),
        texte: ligne,
        taille,
        ancre,
        police: 'main',
        ton: 'accent',
    }));
}
/* ───────────────────────────────────────────────────────── les formules */
/** Met en page une formule ; `null` si elle sort de la liste fermée. */
export function disposerFormule(tex) {
    const segments = analyserTexteLatex(`\\(${tex}\\)`);
    const seul = segments.length === 1 ? segments[0] : undefined;
    if (seul === undefined || seul.type !== 'expression')
        return null;
    return disposerMath(seul.noeud);
}
/** Pose une formule ancrée en `x`, ligne de base `y`. */
function ecrit(c, ou, x, y, tex, taille, ancre, ton) {
    const boite = disposerFormule(tex);
    if (boite === null) {
        c.griefs.push(`${ou} : la formule « ${tex} » ne se dessine pas`);
        return;
    }
    const largeur = boite.largeur * taille;
    const gauche = ancre === 'debut' ? x : ancre === 'milieu' ? x - largeur / 2 : x - largeur;
    c.traces.push({ type: 'formule', x: gauche, y, taille, boite, ton });
}
/** Pose une formule CENTRÉE sur (`cx`, `cy`) — le centre de son encre, pas sa ligne de base. */
function ecritCentre(c, ou, cx, cy, tex, taille, tailleSiHaute, ton) {
    const boite = disposerFormule(tex);
    if (boite === null) {
        c.griefs.push(`${ou} : la formule « ${tex} » ne se dessine pas`);
        return;
    }
    // Une écriture à étages (une fraction) prend le corps réduit : elle occupe
    // déjà deux lignes.
    const t = boite.montee + boite.descente > 1.2 ? tailleSiHaute : taille;
    const y = cy + ((boite.montee - boite.descente) / 2) * t;
    c.traces.push({ type: 'formule', x: cx - (boite.largeur * t) / 2, y, taille: t, boite, ton });
}
/* ─────────────────────────────────────────────────────────── les objets */
const COTE_JETON = 34;
const PAS_JETON = 8;
const SURFACE_JETON = {
    accent: 'jeton_accent',
    papier: 'jeton_papier',
    efface: 'jeton_efface',
};
const ENCRE_JETON = {
    accent: 'accent',
    papier: 'encre',
    efface: 'doux',
};
function jeton(c, x, y, cote, texte, ton, barre) {
    c.traces.push({
        type: 'rect',
        x,
        y,
        largeur: cote,
        hauteur: cote,
        rayon: (9 / COTE_JETON) * cote,
        surface: SURFACE_JETON[ton],
    });
    c.traces.push({
        type: 'texte',
        x: x + cote / 2,
        y: y + cote / 2 + cote * 0.2,
        texte,
        taille: cote * 0.6,
        ancre: 'milieu',
        police: 'math',
        ton: ENCRE_JETON[ton],
    });
    if (barre)
        c.traces.push(segment(x + 3, y + cote - 3, x + cote - 3, y + 3, 'main'));
}
/** L'étendue d'une rangée de jetons : où commence le premier, où finit le dernier. */
export function etendueJetons(x, n, cote = COTE_JETON, pas = PAS_JETON) {
    const xs = Array.from({ length: n }, (_, i) => x + i * (cote + pas));
    return { xs, debut: x, fin: x + n * cote + Math.max(0, n - 1) * pas };
}
/** Largeur d'un paquet de `k` jetons, marge comprise. */
export function largeurPaquet(k) {
    return k * COTE_JETON + Math.max(0, k - 1) * PAS_JETON + 16;
}
export const HAUTEUR_PAQUET = COTE_JETON + 16;
/** Les abscisses des chiffres d'un nombre, et celles où la virgule peut se tenir. */
export function placerChiffres(x, chiffres) {
    const PAS = 30;
    const TRANCHE = 8;
    const places = [];
    let curseur = x;
    let premier = true;
    for (const c of chiffres) {
        if (c === ' ') {
            curseur += TRANCHE;
            continue;
        }
        if (!premier)
            curseur += PAS;
        premier = false;
        places.push({ c, x: curseur });
    }
    const arrets = [];
    for (let i = 0; i <= places.length; i += 1) {
        const avant = places[i - 1];
        const apres = places[i];
        if (avant === undefined && apres !== undefined)
            arrets.push(apres.x - 15);
        else if (avant !== undefined && apres === undefined)
            arrets.push(avant.x + 15.5);
        else if (avant !== undefined && apres !== undefined)
            arrets.push((avant.x + apres.x) / 2);
    }
    return { chiffres: places, arrets };
}
function resoudreElement(c, e, ou) {
    switch (e.type) {
        case 'ecrit':
            ecrit(c, ou, e.x, e.y, e.tex, e.taille ?? 22, e.ancre ?? 'debut', e.ton ?? 'encre');
            return;
        case 'main':
            c.traces.push(...main(e.x, e.y, e.texte, e.taille ?? 21, e.ancre ?? 'debut'));
            return;
        case 'etiquette':
            c.traces.push({
                type: 'texte',
                x: e.x,
                y: e.y,
                texte: e.texte,
                taille: 13,
                ancre: e.ancre ?? 'debut',
                police: e.fort === true ? 'etiquette_forte' : 'etiquette',
                ton: e.fort === true ? 'encre' : 'doux',
            });
            return;
        case 'accolade':
            c.traces.push({
                type: 'chemin',
                d: cheminAccolade(e.x1, e.x2, e.y, e.sens ?? 'bas'),
                trait: e.ton ?? 'main',
            });
            return;
        case 'fleche':
            c.traces.push(...fleche(e.de, e.par, e.a, e.ton ?? 'main'));
            return;
        case 'trait':
            c.traces.push(segment(e.de[0], e.de[1], e.a[0], e.a[1], e.ton ?? 'encre'));
            return;
        case 'zone':
            c.traces.push({
                type: 'rect',
                x: e.x,
                y: e.y,
                largeur: e.largeur,
                hauteur: e.hauteur,
                rayon: e.rayon ?? 0,
                surface: e.ton,
            });
            return;
        case 'jetons': {
            const cote = e.cote ?? COTE_JETON;
            const { xs } = etendueJetons(e.x, e.n, cote, e.pas ?? PAS_JETON);
            const simplifies = new Set(e.simplifies ?? []);
            xs.forEach((x, i) => {
                const barre = simplifies.has(i);
                jeton(c, x, e.y, cote, e.texte, barre ? 'efface' : (e.ton ?? 'accent'), barre);
            });
            return;
        }
        case 'paquets': {
            const largeur = largeurPaquet(e.contenu.length);
            for (let i = 0; i < e.n; i += 1) {
                const x = e.x + i * (largeur + (e.ecart ?? 16));
                c.traces.push({
                    type: 'rect',
                    x,
                    y: e.y,
                    largeur,
                    hauteur: HAUTEUR_PAQUET,
                    rayon: 13,
                    surface: 'paquet',
                });
                e.contenu.forEach((j, k) => {
                    jeton(c, x + 8 + k * (COTE_JETON + PAS_JETON), e.y + 8, COTE_JETON, j.texte, j.ton ?? 'accent', false);
                });
            }
            return;
        }
        case 'groupes': {
            const largeur = e.points * 24 + 18;
            const pas = largeur + 30;
            for (let i = 0; i < e.n; i += 1) {
                const x = e.x + i * pas;
                c.traces.push({
                    type: 'rect',
                    x,
                    y: e.y,
                    largeur,
                    hauteur: 36,
                    rayon: 18,
                    surface: 'teinte',
                });
                for (let k = 0; k < e.points; k += 1) {
                    c.traces.push({ type: 'cercle', cx: x + 21 + k * 24, cy: e.y + 18, r: 7, ton: 'accent' });
                }
                if (i < e.n - 1) {
                    ecrit(c, ou, x + largeur + 15, e.y + 25, e.signe ?? '+', 20, 'milieu', 'doux');
                }
            }
            return;
        }
        case 'arbre': {
            const PAS_X = 48;
            const ECART = 16;
            const feuilles = e.facteur ** e.niveaux;
            // Les ordonnées de chaque étage, des feuilles vers la racine : un nœud
            // se tient au milieu de ses enfants.
            const etages = [];
            etages[e.niveaux] = Array.from({ length: feuilles }, (_, i) => e.y + (i - (feuilles - 1) / 2) * ECART);
            for (let k = e.niveaux - 1; k >= 0; k -= 1) {
                const dessous = etages[k + 1] ?? [];
                const ici = [];
                for (let i = 0; i < dessous.length; i += e.facteur) {
                    const groupe = dessous.slice(i, i + e.facteur);
                    ici.push(groupe.reduce((s, v) => s + v, 0) / groupe.length);
                }
                etages[k] = ici;
            }
            const X = (k) => e.x + k * PAS_X;
            for (let k = 0; k < e.niveaux; k += 1) {
                const parents = etages[k] ?? [];
                (etages[k + 1] ?? []).forEach((y, i) => {
                    const parent = parents[Math.floor(i / e.facteur)] ?? e.y;
                    c.traces.push(segment(X(k), parent, X(k + 1), y, 'doux'));
                });
            }
            etages.forEach((ys, k) => {
                const feuille = k === e.niveaux;
                for (const y of ys) {
                    c.traces.push({
                        type: 'cercle',
                        cx: X(k),
                        cy: y,
                        r: feuille ? 5.5 : 5,
                        ton: feuille ? 'accent' : 'doux',
                    });
                }
            });
            if (e.etiquette !== undefined) {
                for (let k = 0; k < e.niveaux; k += 1) {
                    c.traces.push(...main((X(k) + X(k + 1)) / 2, e.y - 34 - k * 14, e.etiquette, 18, 'milieu'));
                }
            }
            return;
        }
        case 'escalier': {
            const L = e.largeurMarche;
            const H = e.hauteurMarche;
            const n = e.marches.length;
            const x = (i) => e.x + i * L;
            const y = (i) => e.y + i * H;
            const contour = [
                [x(0), e.sol],
                [x(0), y(0)],
            ];
            for (let i = 0; i < n; i += 1) {
                contour.push([x(i + 1), y(i)]);
                if (i < n - 1)
                    contour.push([x(i + 1), y(i + 1)]);
            }
            contour.push([x(n), e.sol]);
            c.traces.push({ type: 'polygone', points: contour, surface: 'papier' });
            if (e.pivot !== undefined) {
                c.traces.push({
                    type: 'rect',
                    x: x(e.pivot),
                    y: y(e.pivot),
                    largeur: L,
                    hauteur: e.sol - y(e.pivot),
                    rayon: 0,
                    surface: 'teinte',
                });
            }
            e.marches.forEach((m, i) => {
                const ton = i === e.pivot ? 'accent' : 'encre';
                ecrit(c, `${ou}.marches[${i}].haut`, x(i) + L / 2, y(i) - 11, m.haut, 23, 'milieu', ton);
                ecritCentre(c, `${ou}.marches[${i}].bas`, x(i) + L / 2, y(i) + 31, m.bas, 25, 20, ton);
                if (i < n - 1 && e.saut !== undefined) {
                    c.traces.push(...fleche([x(i) + L * 0.77, y(i) - 22], [x(i) + L + 14, y(i) - 26], [x(i) + L + 19, y(i + 1) - 23], 'main'));
                    c.traces.push(...main(x(i) + L + 9, y(i) - 33, e.saut, 19, 'milieu'));
                }
            });
            return;
        }
        case 'virgule': {
            const { chiffres, arrets } = placerChiffres(e.x, e.chiffres);
            for (const ch of chiffres) {
                c.traces.push({
                    type: 'texte',
                    x: ch.x,
                    y: e.y,
                    texte: ch.c,
                    taille: 28,
                    ancre: 'milieu',
                    police: 'math',
                    ton: 'encre',
                });
            }
            const xDepart = arrets[e.depart];
            const xArrivee = arrets[e.arrivee];
            if (xDepart === undefined || xArrivee === undefined) {
                c.griefs.push(`${ou} : la virgule sort du nombre`);
                return;
            }
            const virgule = (x, taille, ton) => ({
                type: 'texte',
                x,
                y: e.y,
                texte: ',',
                taille,
                ancre: 'milieu',
                police: 'math',
                ton,
            });
            c.traces.push(virgule(xDepart, 28, 'doux'), virgule(xArrivee, 32, 'accent'));
            const sens = e.arrivee > e.depart ? 1 : -1;
            const ySaut = e.y + 12;
            let rang = 1;
            for (let i = e.depart; i !== e.arrivee; i += sens) {
                const x1 = arrets[i] ?? 0;
                const x2 = arrets[i + sens] ?? 0;
                const m = (x1 + x2) / 2;
                c.traces.push({
                    type: 'chemin',
                    d: `M${pt(x1, ySaut)} Q${pt(m, ySaut + 26)} ${pt(x2, ySaut)}`,
                    trait: 'main',
                });
                if (i + sens === e.arrivee) {
                    c.traces.push({
                        type: 'chemin',
                        d: `M${pt(x2 - sens * 8.5, ySaut + 2.5)} L${pt(x2, ySaut)} ` +
                            `L${pt(x2 - sens * 2.5, ySaut + 9.5)}`,
                        trait: 'main',
                    });
                }
                c.traces.push(...main(m, ySaut + 31, String(rang), 16, 'milieu'));
                rang += 1;
            }
            return;
        }
        case 'axe': {
            const unite = (e.x2 - e.x1) / (e.a - e.de);
            const x = (v) => e.x1 + (v - e.de) * unite;
            c.traces.push(segment(e.x1, e.y, e.x2, e.y, 'encre'));
            for (let v = Math.ceil(e.de); v <= e.a; v += 1) {
                const demi = v === e.origine ? 7 : 4;
                c.traces.push(segment(x(v), e.y - demi, x(v), e.y + demi, 'encre'));
            }
            e.reperes.forEach((r, i) => {
                const premier = i === 0;
                const dernier = i === e.reperes.length - 1;
                const ancre = dernier ? 'fin' : premier ? 'debut' : 'milieu';
                const xt = dernier ? x(r.valeur) + 28 : premier ? x(r.valeur) - 28 : x(r.valeur);
                const haut = r.cote === 'haut';
                c.traces.push(segment(x(r.valeur), haut ? e.y - 30 : e.y + 30, x(r.valeur), e.y, 'main'));
                c.traces.push({ type: 'cercle', cx: x(r.valeur), cy: e.y, r: 4.5, ton: 'accent' });
                c.traces.push({
                    type: 'texte',
                    x: xt,
                    y: haut ? e.y - 58 : e.y + 46,
                    texte: r.nom,
                    taille: 13,
                    ancre,
                    police: 'etiquette_forte',
                    ton: 'encre',
                });
                if (r.tex !== undefined) {
                    ecrit(c, `${ou}.reperes[${i}].tex`, xt, haut ? e.y - 38 : e.y + 67, r.tex, 15, ancre, 'accent');
                }
            });
            return;
        }
        case 'grille': {
            const largeur = e.colonnes * e.cote;
            const hauteur = e.rangees * e.cote;
            c.traces.push({
                type: 'rect',
                x: e.x,
                y: e.y,
                largeur,
                hauteur,
                rayon: 0,
                surface: 'teinte',
            });
            for (let i = 1; i < e.colonnes; i += 1) {
                c.traces.push(segment(e.x + i * e.cote, e.y, e.x + i * e.cote, e.y + hauteur, 'quadrille'));
            }
            for (let i = 1; i < e.rangees; i += 1) {
                c.traces.push(segment(e.x, e.y + i * e.cote, e.x + largeur, e.y + i * e.cote, 'quadrille'));
            }
            return;
        }
        case 'cube': {
            const a = e.arete;
            const f = e.fuite ?? a * 0.57;
            const n = e.n;
            const A = n * a;
            const F = n * f;
            c.traces.push({
                type: 'polygone',
                points: [
                    [e.x, e.y],
                    [e.x + F, e.y - F],
                    [e.x + F + A, e.y - F],
                    [e.x + A, e.y],
                ],
                surface: 'teinte_forte',
            });
            c.traces.push({
                type: 'polygone',
                points: [
                    [e.x + A, e.y],
                    [e.x + A + F, e.y - F],
                    [e.x + A + F, e.y - F + A],
                    [e.x + A, e.y + A],
                ],
                surface: 'teinte_forte',
            });
            c.traces.push({
                type: 'rect',
                x: e.x,
                y: e.y,
                largeur: A,
                hauteur: A,
                rayon: 0,
                surface: 'teinte',
            });
            for (let i = 1; i < n; i += 1) {
                // Face avant.
                c.traces.push(segment(e.x + i * a, e.y, e.x + i * a, e.y + A, 'quadrille'));
                c.traces.push(segment(e.x, e.y + i * a, e.x + A, e.y + i * a, 'quadrille'));
                // Dessus : les fuyantes, puis les parallèles à l'arête avant.
                c.traces.push(segment(e.x + i * a, e.y, e.x + i * a + F, e.y - F, 'quadrille'));
                c.traces.push(segment(e.x + i * f, e.y - i * f, e.x + i * f + A, e.y - i * f, 'quadrille'));
                // Côté droit : les fuyantes, puis les verticales.
                c.traces.push(segment(e.x + A, e.y + i * a, e.x + A + F, e.y + i * a - F, 'quadrille'));
                c.traces.push(segment(e.x + A + i * f, e.y - i * f, e.x + A + i * f, e.y - i * f + A, 'quadrille'));
            }
            return;
        }
    }
}
/**
 * Résout un dessin de fiche. Ne lève jamais : un élément qui ne se dessine pas
 * (une formule hors liste) est omis et signalé dans `griefs` — le validateur
 * refuse alors la fiche ; le rendu, lui, montre le reste plutôt que rien.
 */
export function resoudreDessin(dessin) {
    const chantier = { traces: [], griefs: [] };
    dessin.elements.forEach((e, i) => resoudreElement(chantier, e, `elements[${i}]`));
    return {
        largeur: dessin.largeur ?? LARGEUR_TOILE,
        hauteur: dessin.hauteur,
        decrit: dessin.decrit,
        traces: chantier.traces,
        griefs: chantier.griefs,
    };
}
/** Chasse moyenne d'un caractère, en proportion du corps — une ESTIMATION, pour le garde-fou. */
const CHASSE = {
    main: 0.36,
    etiquette: 0.54,
    etiquette_forte: 0.58,
    math: 0.6,
};
function nombres(d) {
    return (d.match(/-?\d+(?:\.\d+)?/g) ?? []).map(Number);
}
/** L'étendue d'une trace. Exacte pour les formes et les formules, estimée pour les textes. */
export function etendueDeTrace(t) {
    switch (t.type) {
        case 'rect':
            return { g: t.x, d: t.x + t.largeur, h: t.y, b: t.y + t.hauteur };
        case 'polygone': {
            const xs = t.points.map((p) => p[0]);
            const ys = t.points.map((p) => p[1]);
            return { g: Math.min(...xs), d: Math.max(...xs), h: Math.min(...ys), b: Math.max(...ys) };
        }
        case 'cercle':
            return { g: t.cx - t.r, d: t.cx + t.r, h: t.cy - t.r, b: t.cy + t.r };
        case 'chemin': {
            const v = nombres(t.d);
            const xs = v.filter((_, i) => i % 2 === 0);
            const ys = v.filter((_, i) => i % 2 === 1);
            return { g: Math.min(...xs), d: Math.max(...xs), h: Math.min(...ys), b: Math.max(...ys) };
        }
        case 'texte': {
            const largeur = [...t.texte].length * CHASSE[t.police] * t.taille;
            const g = t.ancre === 'debut' ? t.x : t.ancre === 'milieu' ? t.x - largeur / 2 : t.x - largeur;
            return { g, d: g + largeur, h: t.y - t.taille * 0.74, b: t.y + t.taille * 0.24 };
        }
        case 'formule':
            return {
                g: t.x,
                d: t.x + t.boite.largeur * t.taille,
                h: t.y - t.boite.montee * t.taille,
                b: t.y + t.boite.descente * t.taille,
            };
    }
}
/**
 * Ce qui déborde de la toile, à `tolerance` près. Un dessin qui sort de son
 * cadre est rogné sur un écran étroit : c'est l'erreur de coordonnée la plus
 * courante d'un auteur, et la seule qu'un schéma ne voit pas.
 */
export function debordementsDeDessin(resolu, tolerance = 4) {
    const griefs = [];
    resolu.traces.forEach((t, i) => {
        const e = etendueDeTrace(t);
        if (e.g < -tolerance ||
            e.h < -tolerance ||
            e.d > resolu.largeur + tolerance ||
            e.b > resolu.hauteur + tolerance) {
            const quoi = t.type === 'texte' ? `« ${t.texte} »` : t.type;
            griefs.push(`trace ${i} (${quoi}) sort de la toile : ` +
                `x ${n1(e.g)}…${n1(e.d)}, y ${n1(e.h)}…${n1(e.b)} ` +
                `pour ${resolu.largeur} × ${resolu.hauteur}`);
        }
    });
    return griefs;
}
//# sourceMappingURL=dessin.js.map